import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import { routeTree } from "./routeTree.gen";
import type { RouterContext } from "./routes/__root";

export const rootElement = document.getElementById("root");
if (rootElement === null) {
  throw new Error("Root element not found");
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0, // Always consider data stale
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        // Don't retry auth errors (401, 403)
        if (
          error instanceof Error &&
          "statusCode" in error &&
          (error.statusCode === 401 || error.statusCode === 403)
        ) {
          return false;
        }
        // Otherwise, retry up to 3 times
        return failureCount < 3;
      },
    },
  },
});

// Create a router with initial auth state
const router = createRouter({
  routeTree,
  context: {
    queryClient,
    auth: {},
  } satisfies RouterContext,
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0, // Always consider preloaded data stale
  defaultStaleTime: 0, // Always consider data stale
});

// Provide type safety for the router
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

createRoot(rootElement).render(
  <StrictMode>
    <TooltipProvider>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </TooltipProvider>
  </StrictMode>,
);
